import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Square, Car, Bath, LayoutGrid } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PropertyConfiguration {
  id: string;
  config_name: string;
  carpet_area_sqft: number | null;
  price: number | null;
  price_per_sqft: number | null;
  parking: number | null;
  bathrooms: number | null;
  balconies: number | null;
  display_order: number | null;
}

interface PropertyConfigDisplayProps {
  propertyId: string;
}

const formatPrice = (val: number | null) => {
  if (!val) return '—';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  return `₹${val.toLocaleString('en-IN')}`;
};

export const PropertyConfigDisplay = ({ propertyId }: PropertyConfigDisplayProps) => {
  const [configs, setConfigs] = useState<PropertyConfiguration[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const { data, error } = await supabase
          .from('property_configurations')
          .select('*')
          .eq('property_id', propertyId)
          .order('display_order', { ascending: true });

        if (error) throw error;
        setConfigs(data || []);
      } catch (err) {
        console.error('Error fetching configs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [propertyId]);

  if (loading || configs.length === 0) return null;

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4">Pricing & Configurations</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-4 py-2.5 font-medium">Config</th>
                <th className="text-right px-4 py-2.5 font-medium">Carpet Area</th>
                <th className="text-right px-4 py-2.5 font-medium">Price</th>
                <th className="text-right px-4 py-2.5 font-medium hidden sm:table-cell">₹/sqft</th>
                <th className="text-center px-4 py-2.5 font-medium hidden sm:table-cell">Parking</th>
                <th className="text-center px-4 py-2.5 font-medium hidden sm:table-cell">Bath</th>
                <th className="text-center px-4 py-2.5 font-medium hidden sm:table-cell">Balcony</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config, i) => (
                <tr key={config.id} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                  <td className="px-4 py-3 font-medium">{config.config_name}</td>
                  <td className="px-4 py-3 text-right">
                    {config.carpet_area_sqft ? `${config.carpet_area_sqft.toLocaleString()} sqft` : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-primary">
                    {formatPrice(config.price)}
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell">
                    {config.price_per_sqft ? `₹${config.price_per_sqft.toLocaleString()}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {config.parking ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {config.bathrooms ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {config.balconies ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
